import React, { useContext } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Code,
  Search,
  Palette,
} from "lucide-react";
import { CanvasContext } from "../../contexts/CanvasContext";
import "./OfferingServices.css";

const ServiceCard = ({ icon: Icon, title, description, variants }) => {
  const { isDarkTheme } = useContext(CanvasContext);

  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      animate={controls}
      initial="hidden"
      variants={variants}
      className={`service-card ${isDarkTheme ? "dark-theme" : ""}`}
    >
      <div className="service-card__icon-wrapper">
        <Icon className="service-card__icon" />
      </div>
      <div className="service-card__content">
        <h3 className="service-card__title">{title}</h3>
        <p className="service-card__description">{description}</p>
        <button className="service-card__link">
          Explore Service
          <svg
            className="service-card__link-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        </button>
      </div>
    </motion.div>
  );
};

const OfferingServices = () => {
  const controls = useAnimation();
  const { isDarkTheme } = useContext(CanvasContext);

  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  const headerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const services = [
    {
      icon: Code,
      title: "Web Development",
      description:
        "Crafting modern, responsive websites using cutting-edge technologies. From simple landing pages to complex web applications, we bring your vision to life.",
      variants: {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
      },
    },
    {
      icon: Search,
      title: "SEO Optimization",
      description:
        "Boost your online presence with data-driven SEO strategies. We help you rank higher, attract quality traffic, and convert visitors into customers.",
      variants: {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } },
      },
    },
    {
      icon: Palette,
      title: "UI/UX Design",
      description:
        "Creating intuitive and engaging user experiences. Our designs combine aesthetics with functionality to deliver memorable digital experiences.",
      variants: {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.4 } },
      },
    },
  ];

  return (
    <section id="services" className={`offering-services ${isDarkTheme ? "dark-theme" : ""}`}>
      <div className="offering-services__background">
        <div className="offering-services__shape shape-1"></div>
        <div className="offering-services__shape shape-2"></div>
      </div>
      <motion.div
        ref={ref}
        animate={controls}
        initial="hidden"
        variants={headerVariants}
        className="offering-services__header"
      >
        <h2 className="offering-services__subtitle">Our Expertise</h2>
        <h1 className="offering-services__title">
          Transforming Ideas into
          <span className="highlight"> Digital Reality</span>
        </h1>
        <p className="offering-services__description">
          We combine creativity with technical excellence to deliver outstanding
          digital solutions that help businesses thrive in the modern web landscape.
        </p>
      </motion.div>
      <div className="offering-services__grid">
        {services.map((service, index) => (
          <ServiceCard key={index} {...service} />
        ))}
      </div>
    </section>
  );
};

export default OfferingServices;
